import { useEffect, useState } from 'react';

/**
 * Hook para animar contadores numéricos com easing
 * Útil para exibir métricas, estatísticas e números que devem animar ao entrar na viewport
 *
 * @param target - Valor final a ser alcançado
 * @param isInView - Se o elemento está visível na viewport (geralmente do useInView)
 * @param duration - Duração da animação em ms (padrão: 2000ms)
 * @param decimals - Número de casas decimais (padrão: 0)
 * @returns Valor animado do contador
 *
 * @example
 * ```tsx
 * const [ref, isInView] = useInView();
 * const animatedValue = useAnimatedCounter(1000, isInView, 2000, 0);
 * return <div>{animatedValue}</div>;
 * ```
 */
export function useAnimatedCounter(
  target: number,
  isInView: boolean,
  duration = 2000,
  decimals = 0
) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const easeOut = 1 - (1 - progress) ** 3;
      const currentValue = startValue + (target - startValue) * easeOut;

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    const frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [target, isInView, duration]);

  return decimals > 0 ? Number(count.toFixed(decimals)) : Math.floor(count);
}
